import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  useMediaQuery,
  Grow,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import AddIcon from "@material-ui/icons/Add";
import PermissionsEditorDialog from "./PermissionsEditorDialog";

const USERNAME_PATTERN = /^[a-z0-9._-]{3,30}$/;
const emptyForm = { name: "", email: "", username: "", password: "", confirmPassword: "", isActive: true, userType: [] };
const CREATE_NEW_TYPE = "__create_new_type__";

// Handles both create and edit - previously these were two separate,
// incomplete flows (a create-only form, and an edit action that just
// console.log'd). User Type is a single-select dropdown in the UI (this
// business assigns exactly one role per user) even though the underlying
// data model and backend still store userType as an array and support more.
const UserFormDialog = ({ open, onClose, user, userTypes, onCreate, onUpdate, onCreateUserType, openSnackbar }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isEdit = !!user;
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createTypeOpen, setCreateTypeOpen] = useState(false);
  // The "create new user type" option lives inside this form's own Select
  // menu - MUI can emit this dialog's close event while that menu closes and
  // the nested create-type dialog opens on top of it. This keeps the form
  // mounted through that handoff instead of it silently disappearing.
  const keepFormOpenRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        username: user.username || "",
        password: "",
        confirmPassword: "",
        isActive: user.isActive,
        userType: Array.isArray(user.userType) ? user.userType : [],
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, user]);

  const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isAdminType = (typeName) => user?.userType?.includes("admin") && typeName === "admin" && isEdit;

  const handleUserTypeChange = (e) => {
    const value = e.target.value;
    if (value === CREATE_NEW_TYPE) {
      keepFormOpenRef.current = true;
      setCreateTypeOpen(true);
      return;
    }
    setForm((prev) => ({ ...prev, userType: value ? [value] : [] }));
  };

  const closeCreateType = () => {
    setCreateTypeOpen(false);
    // Give the Select's own menu-close a tick to finish before this form's
    // Dialog is allowed to react to close events again.
    setTimeout(() => {
      keepFormOpenRef.current = false;
    }, 0);
  };

  const handleSaveNewType = async ({ name, permissions }) => {
    const created = await onCreateUserType?.({ name, permissions, active: true }, openSnackbar);
    if (created?.name) {
      setForm((prev) => ({ ...prev, userType: [created.name] }));
    }
    closeCreateType();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return openSnackbar("Name is required");
    if (!form.email.trim()) return openSnackbar("Email is required");
    const username = form.username.trim().toLowerCase();
    if (username && !USERNAME_PATTERN.test(username)) {
      return openSnackbar("Username must be 3-30 characters: letters, numbers, dot, dash or underscore");
    }
    if (!form.userType.length) return openSnackbar("Select at least one user type");

    setSaving(true);
    if (isEdit) {
      // Awaited (was fire-and-forget) so the Save button's spinner reflects
      // the actual request, and a failed update keeps the dialog open with
      // the edits intact instead of closing regardless of the outcome.
      onUpdate(
        user._id,
        { name: form.name.trim(), email: form.email.trim(), username, isActive: form.isActive, userType: form.userType },
        openSnackbar
      )
        .then(() => onClose())
        .catch(() => {})
        .finally(() => setSaving(false));
    } else {
      if (!form.password || form.password.length < 6) {
        setSaving(false);
        return openSnackbar("Password must be at least 6 characters");
      }
      if (form.password !== form.confirmPassword) {
        setSaving(false);
        return openSnackbar("Passwords don't match");
      }
      const [firstName, ...rest] = form.name.trim().split(" ");
      onCreate(
        {
          firstName,
          lastName: rest.join(" "),
          email: form.email.trim(),
          username,
          password: form.password,
          confirmPassword: form.confirmPassword,
          userType: form.userType,
        },
        openSnackbar,
        setSaving,
        onClose
      );
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!keepFormOpenRef.current) onClose();
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Grow}
        BackdropProps={{ style: { backdropFilter: "blur(3px)", backgroundColor: "rgba(15,23,42,0.35)" } }}
        PaperProps={{ style: { borderRadius: isMobile ? 0 : 16 } }}
      >
        <DialogTitle style={{ background: "linear-gradient(135deg, #ef7f1a 0%, #ff9800 100%)", color: "#fff" }}>
          {isEdit ? `Edit User — ${user?.name || form.name || ""}` : "Create User"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent style={{ paddingTop: 20 }}>
            {isEdit && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                  marginBottom: 16,
                  fontSize: "0.8125rem",
                }}
              >
                <div>
                  <span style={{ color: "#94a3b8", fontWeight: 500 }}>User: </span>
                  <strong style={{ color: "#1e293b" }}>{user?.name || form.name}</strong>
                  {(user?.email || form.email) && (
                    <span style={{ color: "#64748b", marginLeft: 6 }}>
                      ({user?.email || form.email})
                    </span>
                  )}
                </div>
                {(user?.userType?.[0] || (Array.isArray(form.userType) ? form.userType[0] : form.userType)) && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      padding: "2px 6px",
                      borderRadius: "4px",
                      backgroundColor: "#e0e7ff",
                      color: "#3730a3",
                    }}
                  >
                    {user?.userType?.[0] || (Array.isArray(form.userType) ? form.userType[0] : form.userType)}
                  </span>
                )}
              </div>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoFocus
                  label="Full Name"
                  variant="outlined"
                  fullWidth
                  value={form.name}
                  onChange={setField("name")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email / Login ID"
                  variant="outlined"
                  fullWidth
                  value={form.email}
                  onChange={setField("email")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Username (optional)"
                  variant="outlined"
                  fullWidth
                  value={form.username}
                  onChange={setField("username")}
                  helperText="A second way to sign in, besides the email. 3-30 letters, numbers, . _ -"
                  inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
                />
              </Grid>

              {!isEdit && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Password"
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      value={form.password}
                      onChange={setField("password")}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPassword((s) => !s)}>
                              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Confirm Password"
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      value={form.confirmPassword}
                      onChange={setField("confirmPassword")}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={isEdit ? 8 : 12}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="user-type-label">User Type</InputLabel>
                  <Select
                    labelId="user-type-label"
                    label="User Type"
                    // Single-select in the UI on purpose: the data model (and
                    // the backend) still stores userType as an array and can
                    // hold more than one role, but picking exactly one here is
                    // simpler for how this business actually assigns roles.
                    value={form.userType[0] || ""}
                    onChange={handleUserTypeChange}
                  >
                    {userTypes && userTypes.length > 0
                      ? userTypes.map((ut) => (
                          <MenuItem key={ut.name} value={ut.name} disabled={!ut.active || isAdminType(ut.name)}>
                            {ut.name}{ut.active ? "" : " (Inactive)"}
                          </MenuItem>
                        ))
                      : [
                          <MenuItem key="none" disabled>
                            No user types available yet
                          </MenuItem>,
                        ]}
                    {onCreateUserType && [
                      <Divider key="divider" />,
                      <MenuItem
                        key="create-new"
                        value={CREATE_NEW_TYPE}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{ color: "#ef7f1a", fontWeight: 600 }}
                      >
                        <AddIcon fontSize="small" style={{ marginRight: 8 }} />
                        Create new user type
                      </MenuItem>,
                    ]}
                  </Select>
                </FormControl>
              </Grid>

              {isEdit && (
                <Grid item xs={12} sm={4} style={{ display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isActive}
                        onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions style={{ padding: "8px 24px 20px" }}>
            <Button onClick={onClose} fullWidth={isMobile} disabled={saving}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              fullWidth={isMobile}
              style={{ backgroundColor: "#ef7f1a", color: "#fff" }}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isEdit ? "Save Changes" : "Create User"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <PermissionsEditorDialog
        open={createTypeOpen}
        isNew
        userType={null}
        duplicateFrom={null}
        onClose={closeCreateType}
        onSave={handleSaveNewType}
      />
    </>
  );
};

export default UserFormDialog;
